<!-- The sidebar's segmented control (Seán, 2026-10-22, issue C1): FAQ · Highlights · Testimonials;
     on a narrow screen led by the page's own title (VIZBI 2026), which brings the page back. The
     chosen one filled with the brand colour, as the site's chosen toggles. The web's tab pattern:
     one Tab stop, ← → move and choose. -->
<template>
  <div
    ref="controlRef"
    class="segmented"
    :class="{isCrowded: tabs.length > 2}"
    role="tablist"
    :aria-label="label"
    @keydown="onKey">
    <!-- the chosen one's fill, sliding from segment to segment (Seán, 2026-10-22) -->
    <span class="fill" :style="fillStyle" aria-hidden="true" />
    <button
      v-for="tab in tabs"
      :id="`tab-${tab.key || 'page'}`"
      :key="tab.key"
      type="button"
      role="tab"
      class="segment"
      :class="{isTitle: tab.isTitle}"
      :aria-selected="tab.key === selected"
      :aria-controls="tab.panelId"
      :tabindex="tab.key === selected ? 0 : -1"
      :aria-label="getIcon(tab) ? tab.label : undefined"
      :title="getIcon(tab) ? tab.label : undefined"
      @click="emit('select', tab.key)">
      <!-- four or more segments (the Masterclass page's narrow control): the page's own as a
           home icon at one end and Testimonials as a large quotation mark at the other, so
           five fit a phone, symmetrically (Seán, 2026-10-28) -->
      <svg v-if="getIcon(tab) === 'home'" class="home" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M3 11.5 12 4l9 7.5M5.5 10v9.5h4.5V14h4v5.5h4.5V10" />
      </svg>
      <svg v-else-if="getIcon(tab) === 'quote'" class="quote" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M10.2 5.2Q3.8 7.4 3.8 14a3.7 3.7 0 1 0 3.7-3.7h-.4q.4-3 3.6-3.9zM21 5.2q-6.4 2.2-6.4 8.8a3.7 3.7 0 1 0 3.7-3.7h-.4q.4-3 3.6-3.9z" />
      </svg>
      <template v-else>{{ tab.label }}</template>
    </button>
  </div>
</template>

<script setup>
import {computed, nextTick, onMounted, ref, watch} from "vue";
import {useResizeObserver} from "@vueuse/core";
const props = defineProps({
  tabs: {type: Array, required: true}, // [{key, label, panelId, isTitle}]
  selected: {type: String, default: ""},
  label: {type: String, default: "Sections"}
});
const emit = defineEmits(["select"]);
// with four or more segments, two are icons: "home" (the page's own) and "quote" (Testimonials)
const isIconic = computed(() => props.tabs.length > 3);
function getIcon(tab) {
  if (!isIconic.value) return "";
  if (tab.isTitle) return "home";
  return tab.key === "testimonials" ? "quote" : "";
}
// the fill under the chosen segment: its place and width, measured (they vary by label and font)
const controlRef = ref(null);
const fillStyle = ref({opacity: 0});
function placeFill() {
  const chosen = controlRef.value?.querySelector('[aria-selected="true"]');
  if (!chosen) return (fillStyle.value = {opacity: 0});
  const {offsetLeft: left, offsetWidth: width} = chosen;
  fillStyle.value = {width: `${width}px`, transform: `translateX(${left}px)`};
}
watch(() => props.selected, () => nextTick(placeFill));
onMounted(placeFill);
useResizeObserver(controlRef, placeFill);
const STEPS = {ArrowRight: 1, ArrowLeft: -1};
// ← → the next or previous segment, chosen and focused: once the choice has rendered (focused
// at once, the focus was lost as the page re-rendered, 2026-10-22)
async function onKey(event) {
  const step = STEPS[event.key];
  if (!step) return;
  event.preventDefault();
  const keys = props.tabs.map((tab) => tab.key);
  const next = (keys.indexOf(props.selected) + step + keys.length) % keys.length;
  emit("select", keys[next]);
  await nextTick();
  document.getElementById(`tab-${keys[next] || "page"}`)?.focus();
}
</script>

<style scoped>
/* its edge a ring a step lighter (dark theme) or darker (light) than its fill (Seán, 2026-10-22:
   too close to the page's colour, the control looked narrower than the dots and text below);
   inset, so it takes no width */
.segmented {
  display: flex;
  padding: 2px;
  background: var(--filter-control-background);
  border-radius: 9px;
  box-shadow: inset 0 0 0 1px var(--filter-control-border);
}
/* a segment: no button look (global.css gives every button one); the chosen one filled. All the
   same width (Seán, 2026-10-22); with three or more (Testimonials, on localhost only for now),
   the text a size smaller, so the longest, bold, fits a third of the column (250 px:
   "Testimonials" at 0.75rem); on a phone the text stays as it is (YearSidebar.vue) */
.segment {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 0;
  min-width: 0;
  padding: 4px;
  font: inherit;
  font-size: 0.85rem;
  font-weight: 500;
  white-space: nowrap;
  color: var(--filter-control-text);
  background: none;
  border: none;
  border-radius: 7px;
  box-shadow: none;
  cursor: pointer;
}
.isCrowded .segment {
  font-size: 0.75rem;
}
.segment.isTitle {
  font-weight: 700;
}
/* an icon's segment: the same width as the others (Seán, 2026-10-28: equal widths where
   there's room; below 360 px each takes its own), the icons sized by the text and centred on
   its line (the segment centres its content), the quotation mark heavy so it reads as one */
.segment:has(.home),
.segment:has(.quote) {
  padding: 4px;
}
.quote {
  display: block;
  width: 1.25em;
  height: 1.25em;
  fill: currentColor;
}
.home {
  display: block;
  width: 1.2em;
  height: 1.2em;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.segment[aria-selected="true"] {
  font-weight: 700;
  color: white;
}
/* the chosen one's fill: behind the segments, sliding to the chosen one */
.segmented {
  position: relative;
}
.segment {
  position: relative;
  z-index: 1;
  transition: color 0.25s ease;
}
.fill {
  position: absolute;
  top: 2px;
  bottom: 2px;
  left: 0;
  background: var(--brand-color);
  border-radius: 7px;
  transition: transform 0.25s ease, width 0.25s ease;
}
@media (prefers-reduced-motion: reduce) {
  .fill,
  .segment {
    transition: none;
  }
}
/* keyboard focus (Seán, 2026-10-22, as #register's: FORMS.md § Focus): one solid ring around the
   whole control, following its corners; ← → then choose within it. Drawn inside its edge, in
   its 2 px padding: outside, the page's #main_content (overflow: hidden) clipped its right side.
   Not on a segment: the global dotted ring (global.css .showFocusOutline, !important) is off */
.segment:focus-visible,
.segment.showFocusOutline {
  outline: none !important;
}
.segmented:has(.segment:focus-visible),
.segmented:has(.segment.showFocusOutline) {
  outline: 2px solid var(--brand-dark-color);
  outline-offset: -2px;
}
/* the smallest phones (320 px): a size smaller and less padding, the widths still equal (with
   the icons at the ends, five fit: 2026-10-28) */
@media (max-width: 359px) {
  .segment {
    padding: 4px 3px;
    font-size: 0.75rem !important;
  }
}
/* hover: only a segment a click would choose, its text at full strength; the chosen one keeps
   its look (DESIGN.md § 15) */
.segment[aria-selected="false"]:hover {
  color: var(--text-color);
}
</style>
